import { useState, useCallback, Fragment } from 'react';
import { Search, Download, Phone, Mail, Loader2, ChevronDown, Calendar, MapPin, Package as PackageIcon, FileText } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import Loader, { ErrorState } from '../components/Loader';
import { formatINR } from '../lib/whatsapp';
import { statusColors } from './sampleInquiries';

const statuses = ['All', 'New', 'Contacted', 'Quoted', 'Closed'];

// How the lead entered the funnel.
const originMeta = {
  package: { label: 'Package View', cls: 'bg-info/15 text-info' },
  customize: { label: 'Customized', cls: 'bg-spectrum-violet/25 text-spectrum-pink' },
  contact: { label: 'Direct Contact', cls: 'bg-warning/15 text-warning' },
};

function toCSV(rows) {
  const headers = ['Name', 'Mobile', 'Email', 'Origin', 'Source', 'Package', 'Add-ons', 'Total', 'Event Date', 'Location', 'Notes', 'Status', 'Created'];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.join(',')].concat(
    rows.map((r) =>
      [
        r.name, r.mobile, r.email,
        (originMeta[r.origin] || originMeta.contact).label,
        r.source === 'app' ? 'App' : 'Web',
        r.package?.title || '',
        (r.addOns || []).map((a) => a.title).join('; '),
        r.total, r.eventDate, r.location, r.notes, r.status,
        new Date(r.createdAt).toISOString().slice(0, 10),
      ].map(escape).join(',')
    )
  );
  return lines.join('\n');
}

export default function AdminInquiries() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('All');
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status !== 'All') params.set('status', status);
    if (query) params.set('q', query);
    return api.get(`/inquiries?${params.toString()}`).then((r) => r.data);
  }, [status, query]);

  const { data, loading, error, reload } = useAsync(load, [status, query]);
  const rows = data || [];

  const updateStatus = async (id, newStatus) => {
    setSavingId(id);
    try {
      await api.put(`/inquiries/${id}`, { status: newStatus });
      reload();
    } finally {
      setSavingId(null);
    }
  };

  const exportCSV = () => {
    const blob = new Blob([toCSV(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Inquiries</h1>
          <p className="text-sm text-ink-400">{rows.length} leads</p>
        </div>
        <button onClick={exportCSV} disabled={!rows.length} className="btn-primary !py-2.5 text-sm disabled:opacity-50">
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 glass px-4 py-2">
          <Search size={16} className="text-ink-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, mobile, package…"
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-ink-500"
          />
        </div>
        <div className="flex gap-1.5">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-pill px-3 py-1.5 text-xs font-medium transition ${
                status === s ? 'bg-brand-gradient text-white' : 'border border-white/10 text-ink-300 hover:text-white'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader label="Loading inquiries…" />
      ) : error ? (
        <ErrorState message="Couldn't load inquiries." onRetry={reload} />
      ) : (
        <div className="glass overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-ink-400">
              <tr>
                <th className="w-8 px-3 py-4"></th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">Origin</th>
                <th className="px-5 py-4">Package</th>
                <th className="px-5 py-4">Event Date</th>
                <th className="px-5 py-4">Location</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => {
                const open = expandedId === r._id;
                const addOns = r.addOns || [];
                return (
                <Fragment key={r._id}>
                <tr
                  onClick={() => setExpandedId(open ? null : r._id)}
                  className="cursor-pointer transition hover:bg-white/[0.03]"
                >
                  <td className="px-3 py-4 text-ink-400">
                    <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-white">{r.name}</p>
                    <p className="flex flex-wrap items-center gap-3 text-xs text-ink-400">
                      <span className="flex items-center gap-1"><Phone size={11} /> {r.mobile}</span>
                      {r.email && <span className="flex items-center gap-1"><Mail size={11} /> {r.email}</span>}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`rounded-pill px-2.5 py-1 text-xs font-medium ${(originMeta[r.origin] || originMeta.contact).cls}`}>
                      {(originMeta[r.origin] || originMeta.contact).label}
                    </span>
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-ink-500">
                      {r.source === 'app' ? 'App' : 'Web'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-ink-200">
                    {r.package?.title || '—'}
                    {addOns.length > 0 && (
                      <span className="ml-2 rounded-pill bg-brand/15 px-2 py-0.5 text-[10px] text-brand-light">
                        +{addOns.length} add-on{addOns.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-ink-300">{r.eventDate || '—'}</td>
                  <td className="px-5 py-4 text-ink-300">{r.location || '—'}</td>
                  <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <select
                        value={r.status}
                        onChange={(e) => updateStatus(r._id, e.target.value)}
                        className={`rounded-pill border-0 px-2.5 py-1 text-xs font-medium outline-none ${statusColors[r.status] || 'bg-white/5 text-ink-200'}`}
                      >
                        {statuses.slice(1).map((s) => (
                          <option key={s} value={s} className="bg-ink-800 text-white">{s}</option>
                        ))}
                      </select>
                      {savingId === r._id && <Loader2 size={14} className="animate-spin text-ink-400" />}
                    </div>
                  </td>
                </tr>

                {open && (
                  <tr className="bg-white/[0.02]">
                    <td colSpan={7} className="px-5 pb-6 pt-1">
                      <div className="grid gap-5 rounded-xl border border-white/10 bg-ink-900/40 p-5 md:grid-cols-2">
                        {/* What they requested */}
                        <div>
                          <h4 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold text-white">
                            <PackageIcon size={15} className="text-brand-light" /> Requested Package
                          </h4>
                          {r.package?.title ? (
                            <div className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm">
                              <span className="text-white">{r.package.title}</span>
                              <span className="text-ink-300">{formatINR(r.package.price)}</span>
                            </div>
                          ) : (
                            <p className="text-sm text-ink-400">No package selected (direct contact).</p>
                          )}

                          {addOns.length > 0 && (
                            <>
                              <p className="mb-2 mt-4 text-xs uppercase tracking-wider text-ink-400">Add-ons chosen</p>
                              <ul className="space-y-1.5">
                                {addOns.map((a, i) => (
                                  <li key={a.id || i} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm">
                                    <span className="text-ink-200">{a.title}</span>
                                    <span className="text-ink-400">{formatINR(a.price)}</span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}

                          <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
                            <span className="text-sm font-semibold text-white">Estimated Total</span>
                            <span className="font-display text-lg font-bold text-gradient">{formatINR(r.total || 0)}</span>
                          </div>
                        </div>

                        {/* Customer + event details */}
                        <div className="space-y-3 text-sm">
                          <h4 className="mb-1 font-display text-sm font-semibold text-white">Event & Contact</h4>
                          <p className="flex items-center gap-2 text-ink-300"><Calendar size={14} /> Event date: <span className="text-white">{r.eventDate || '—'}</span></p>
                          <p className="flex items-center gap-2 text-ink-300"><MapPin size={14} /> Location: <span className="text-white">{r.location || '—'}</span></p>
                          <p className="flex items-center gap-2 text-ink-300"><Phone size={14} /> {r.mobile}</p>
                          {r.email && <p className="flex items-center gap-2 text-ink-300"><Mail size={14} /> {r.email}</p>}
                          <div className="flex items-start gap-2 text-ink-300">
                            <FileText size={14} className="mt-0.5" />
                            <span>Notes: <span className="text-white">{r.notes || '—'}</span></span>
                          </div>
                          <p className="pt-2 text-xs text-ink-500">
                            Submitted {new Date(r.createdAt).toLocaleString('en-IN')} · via {r.source === 'app' ? 'Mobile App' : 'Website'} · {(originMeta[r.origin] || originMeta.contact).label}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <p className="py-12 text-center text-ink-400">No inquiries match your filters.</p>}
        </div>
      )}
    </div>
  );
}
